// Every trip leg and logbook entry that refers to one gazetteer place.
//
// THERE IS NO PLACE-KEYED QUERY, AND THERE CANNOT BE ONE. A waypoint's binding
// lives inside an AWSJSON `fields` blob, which DynamoDB cannot index, so the
// only way to answer "what have I logged at Cap Sante" is to walk both tables
// and ask each record. That is a fan-out of `1 + 3N` paged queries -- the item
// list, then per item its trips, its entries and its logbooks -- run
// FLEET_READ_CONCURRENCY items at a time so a large fleet is a queue rather
// than a burst. Don't go looking for the index that should exist;
// scripts/merge-places.mjs walks the same two tables in full for the same
// reason, and says so at similar length.
//
// `listCompletedTasks(itemId)` rather than a chain per logbook: that
// operation's selection set already carries `logbookId` AND `fields` (see the
// note above APICompletedTasksByItemId), so one read covers every logbook the
// item has. `listLogbooks` is still needed, but only for names and types.
//
// Returns { place, records, failures, StatusMessage }. A record is
//   { kind: 'entry' | 'trip', id, date, itemId, item, logbookId, logbook,
//     record, display, usage }
// where `usage` is PlaceUsage's answer (the resolved route included) and
// `display` is the gazetteer-resolved copy the table renders. `record` is the
// STORED row -- what a navigation must be built from.

import { useEffect, useState } from 'react';
import { useStatusMessage } from '../../components';
import * as Database from '../../utilities/Database.mjs';
import { saveQueue } from '../../utilities/SaveQueue.mjs';
import { RECORD_LANE_PREFIXES } from '../../utilities/saveLanes';
import { tripPlaceUsage, entryPlaceUsage } from '../../fleet-shared/PlaceUsage.mjs';
import { mapWithConcurrency, FLEET_READ_CONCURRENCY } from '../../fleet-shared/Concurrency.mjs';
import {
  parseTripExtraFields,
  tripWithResolvedEndpoints,
  entryWithResolvedEndpoints,
} from '../../fleet-shared/TripRoute.mjs';

// Newest first, the way every list in the app reads. These arrive from many
// queries at once, so there is no source order to mirror -- unlike a logbook
// page, which reverses to preserve same-date ties.
function byDateDescending(a, b) {
  return (Date.parse(b.date) || 0) - (Date.parse(a.date) || 0);
}

// A trip mirrors its anchors into COLUMNS and an entry into the same blob its
// route sits in. Every consumer here wants the pair and none of them wants to
// care which table it came from, so the difference is flattened once, on the
// display copy (so a renamed place reaches the cells as well as the map).
function displayEndpoints(display, kind) {
  const stored = kind === 'entry' ? parseTripExtraFields(display?.fields) : display;
  return { from: stored?.from ?? null, to: stored?.to ?? null };
}

export function usePlaceRecords(placeId, placeIndex) {
  const [StatusMessage, { showText, showError, hideStatus }] = useStatusMessage();
  const [place, setPlace] = useState(null);
  const [records, setRecords] = useState(null);
  const [failures, setFailures] = useState([]);

  useEffect(() => {
    // WAIT FOR THE GAZETTEER. usePlaceIndex resolves to null until the table
    // lands, and here the index is a MATCH input, not just a display one: with
    // no index nothing resolves, a name-only reference has no owner to check
    // against, and the page would under-report and then silently correct
    // itself. Re-running on arrival would fix the answer -- and run the whole
    // 1+3N fan-out a second time to do it.
    if (!placeIndex) return undefined;

    let mounted = true;
    // Aborting cancels nothing in flight -- Database.* takes no signal. Its one
    // job is dropping the settledAll subscription below, so a lane that never
    // drains can't hold it for the life of the tab. The `mounted` re-check
    // after every await is what actually protects the state.
    const leaving = new AbortController();

    async function load() {
      try {
        showText('Loading...');

        // BEFORE the first await, and this one is mandatory rather than
        // defensive: `place.nameKey` IS the match key. A read that beats a
        // rename still sitting in the autosave debounce would match every
        // record in the fleet against the OLD name, and the page would come
        // back wrong rather than merely stale. Browser Back from the edit page
        // starts that flush but doesn't finish it (see Places.jsx).
        //
        // The trip and entry lanes matter for the same reason one step down: a
        // route edit still in flight is a binding this page would miss.
        //
        // A lane parked in error returns immediately -- isPending excludes them
        // -- which is correct: waiting won't change the answer, and
        // SaveStatusPill is already showing Retry.
        saveQueue.flushAll();
        await saveQueue.settledAll(
          saveQueue
            .pendingKeys()
            .filter((key) => RECORD_LANE_PREFIXES.some((prefix) => key.startsWith(prefix))),
          { signal: leaving.signal }
        );
        if (!mounted) return;

        // The row itself, read directly -- NOT placeIndex.byId. loadPlaceIndex
        // swallows a failure into empty maps, so deriving the row from it would
        // render "place not found" for a network blip.
        const loadedPlace = await Database.getPlace(placeId);
        if (!mounted) return;
        setPlace(loadedPlace);
        if (!loadedPlace) { hideStatus(); setRecords([]); return; }

        // Only this one is fatal: with no item list there is nothing to walk.
        const items = await Database.listAllItems();
        if (!mounted) return;

        let done = 0;
        const failed = [];
        // A few items at a time: each is three reads, and a bare Promise.all
        // over the fleet is a burst of 3N simultaneous AppSync requests. The
        // progress counter below reads the same either way, only steadier.
        const perItem = await mapWithConcurrency(
          items,
          FLEET_READ_CONCURRENCY,
          async (item) => {
            try {
              const [trips, entries, logbooks] = await Promise.all([
                Database.listTripsForItem(item.id),
                Database.listCompletedTasks(item.id),
                Database.listLogbooks(item.id),
              ]);
              return { item, trips, entries, logbooks };
            } catch (err) {
              // One item's read failing must not cost the user the other
              // thirteen. Collected and reported above the table; never
              // showError, which would replace the whole page.
              console.warn(`Couldn't read ${item.name}:`, err);
              failed.push({ item, err });
              return null;
            } finally {
              // Counted on COMPLETION -- the reads finish out of order, so a
              // position would jump around.
              done += 1;
              if (mounted) showText(`Searching ${done} of ${items.length} items...`);
            }
          },
          // Unmounting stops new items being read; `perItem` then has holes,
          // which the `if (!bucket) continue` below already skips -- though
          // the `!mounted` check right after makes that moot.
          { signal: leaving.signal }
        );
        if (!mounted) return;

        const index = placeIndex;
        const found = [];
        for (const bucket of perItem) {
          if (!bucket) continue;
          const { item, trips, entries, logbooks } = bucket;
          const logbookById = new Map((logbooks || []).map((lb) => [lb.id, lb]));

          for (const trip of trips || []) {
            const usage = tripPlaceUsage(trip, loadedPlace, index);
            if (!usage) continue;
            const display = tripWithResolvedEndpoints(trip, index);
            found.push({
              kind: 'trip',
              id: trip.id,
              date: trip.date,
              itemId: item.id,
              item,
              logbookId: null,
              logbook: null,
              record: trip,
              display,
              ...displayEndpoints(display, 'trip'),
              usage,
            });
          }

          for (const entry of entries || []) {
            const usage = entryPlaceUsage(entry, loadedPlace, index);
            if (!usage) continue;
            const display = entryWithResolvedEndpoints(entry, index);
            found.push({
              kind: 'entry',
              id: entry.id,
              date: entry.date,
              itemId: item.id,
              item,
              logbookId: entry.logbookId,
              logbook: logbookById.get(entry.logbookId) || null,
              record: entry,
              display,
              ...displayEndpoints(display, 'entry'),
              usage,
            });
          }
        }

        found.sort(byDateDescending);
        setRecords(found);
        setFailures(failed);
        hideStatus();
      } catch (err) {
        if (mounted) showError(err);
      }
    }

    load();
    return () => { mounted = false; leaving.abort(); };
    // usePlaceIndex is per-mount and its value lands once, so `placeIndex` here
    // means "run when the gazetteer arrives" and nothing more.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeId, placeIndex]);

  return { place, records, failures, StatusMessage };
}

export default usePlaceRecords;
